import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';

async function makeThumbnail(file, size = 56) {
  const bitmap = await createImageBitmap(file, {
    resizeWidth: size,
    resizeHeight: size,
    resizeQuality: 'medium',
  });
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  canvas.getContext('2d').drawImage(bitmap, 0, 0);
  bitmap.close();
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
}

function generateId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return `file-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getSingleUploadResult(result, file) {
  const uploadResults = Array.isArray(result?.upload_results) ? result.upload_results : [];
  const matched = uploadResults.find((item) => item?.filename === file.name) || uploadResults[0] || null;
  const serverPath = matched?.attachment || result?.attachments?.[0] || null;
  const status = matched?.status || (serverPath ? 'uploaded' : 'failed');
  const message = matched?.message || result?.message || null;
  return { serverPath, status, message };
}

/** 從 attachment URL 解析 upload_id query param；解析失敗回傳 null */
function parseUploadId(attachmentUrl) {
  if (!attachmentUrl) return null;
  try {
    const qIndex = attachmentUrl.indexOf('?');
    if (qIndex < 0) return null;
    return new URLSearchParams(attachmentUrl.slice(qIndex + 1)).get('upload_id') || null;
  } catch {
    return null;
  }
}

/**
 * 管理選檔後立即上傳的狀態。每個 FileEntry 獨立追蹤上傳進度，
 * 支援任意狀態下移除（含上傳中取消）。
 *
 * @param {object} options
 * @param {number} [options.maxTotal=6] 最多允許的檔案總數
 */
export function useFileUpload({ maxTotal = 6 } = {}) {
  const [entries, setEntries] = useState([]);
  /** remove API 失敗時的通知文字（null 表示無通知） */
  const [removeNotice, setRemoveNotice] = useState(null);
  /** 重複檔案被阻擋時的通知文字（null 表示無通知） */
  const [duplicateNotice, setDuplicateNotice] = useState(null);
  // ref 供 async callback 在 setState 之外讀取最新 entries
  const entriesRef = useRef([]);

  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  // 上傳單一 entry；settled 後確認 entry 仍在 state 才更新
  const uploadEntry = useCallback(async (entry, chatId) => {
    try {
      const result = await apiService.uploadFiles([entry.file], chatId, entry.abortController.signal);
      const uploadResult = getSingleUploadResult(result, entry.file);
      const isFailed = uploadResult.status === 'failed' || !uploadResult.serverPath;
      setEntries((prev) => {
        if (!prev.some((e) => e.id === entry.id)) return prev; // removed/cancelled
        return prev.map((e) =>
          e.id === entry.id
            ? {
                ...e,
                uploadStatus: isFailed ? 'error' : 'done',
                serverPath: uploadResult.serverPath,
                // 上傳成功時從 attachment URL 解析 upload_id，供後續 remove API 使用
                uploadId: isFailed ? null : parseUploadId(uploadResult.serverPath),
                errorMessage: isFailed ? (uploadResult.message || '檔案上傳失敗') : null,
                uploadMessage: !isFailed ? uploadResult.message : null,
              }
            : e
        );
      });
    } catch (err) {
      // AbortError = 使用者取消，靜默忽略（entry 已從 state 移除）
      if (err.name === 'AbortError' || entry.abortController.signal.aborted) return;
      // 瀏覽器原生網路錯誤（"Failed to fetch" / "Load failed"）顯示友善文案
      const isNetworkError =
        err.name === 'TypeError' ||
        /failed to fetch|networkerror|load failed/i.test(String(err.message));
      const errorMessage = isNetworkError ? '上傳中斷，請重新選取檔案' : (err.message || '上傳失敗');
      setEntries((prev) => {
        if (!prev.some((e) => e.id === entry.id)) return prev;
        return prev.map((e) =>
          e.id === entry.id
            ? { ...e, uploadStatus: 'error', errorMessage }
            : e
        );
      });
    }
  }, []);

  /**
   * 加入已通過前端驗證的檔案並立即觸發上傳。
   * 圖片縮圖非同步生成，chip 先出現後再更新縮圖。
   * 若檔案已存在於本次問答（name + size 相符），直接阻擋並設定 duplicateNotice。
   *
   * @param {File[]} files   已通過驗證的 File 物件陣列
   * @param {string} chatId  目前對話 ID（作為 sessionId）
   */
  const addFiles = useCallback(
    (files, chatId) => {
      if (!files.length) return;

      // 過濾掉與現有 entries 重複的檔案（name + size 同時相符視為重複）
      const existing = entriesRef.current;
      const duplicates = [];
      const uniqueFiles = files.filter((file) => {
        const isDuplicate = existing.some(
          (e) => e.file.name === file.name && e.file.size === file.size
        );
        if (isDuplicate) duplicates.push(file.name);
        return !isDuplicate;
      });

      if (duplicates.length > 0) {
        const names = duplicates.map((n) => `「${n}」`).join('、');
        setDuplicateNotice(`${names} 已在此次問答中附加，不可重複上傳。`);
      }

      if (!uniqueFiles.length) return;

      const newEntries = uniqueFiles.map((file) => ({
        id: generateId(),
        file,
        previewUrl: null,
        isImage: file.type.startsWith('image/'),
        uploadStatus: 'uploading',
        serverPath: null,
        uploadId: null, // 上傳成功後由 settled handler 填入（attachment URL 的 upload_id param）
        errorMessage: null,
        uploadMessage: null,
        abortController: new AbortController(),
      }));

      setEntries((prev) => [...prev, ...newEntries]);

      for (const entry of newEntries) {
        // 立即開始上傳
        uploadEntry(entry, chatId);

        // 圖片：非同步生成縮圖，完成後補 previewUrl
        if (entry.isImage) {
          (async () => {
            let url;
            try {
              const blob = await makeThumbnail(entry.file);
              url = URL.createObjectURL(blob);
            } catch {
              url = URL.createObjectURL(entry.file);
            }
            setEntries((prev) => {
              if (!prev.some((e) => e.id === entry.id)) {
                URL.revokeObjectURL(url);
                return prev;
              }
              return prev.map((e) => (e.id === entry.id ? { ...e, previewUrl: url } : e));
            });
          })();
        }
      }
    },
    [uploadEntry]
  );

  /**
   * 移除指定 entry（任何狀態均可）。
   * 上傳進行中時先 abort，再移除。
   * 上傳已完成（done）時，在本地移除後額外呼叫後端 remove API 清除 GenAIus session asset。
   *
   * @param {string} id     - 要移除的 entry id
   * @param {string} chatId - 對話 ID（供 remove API 的 session_id 使用）
   */
  const removeEntry = useCallback((id, chatId) => {
    // entriesRef.current 同步可讀，不依賴 React 18 async updater 的執行時機
    const entry = entriesRef.current.find((e) => e.id === id);
    if (!entry) return;

    if (entry.uploadStatus === 'uploading') entry.abortController.abort();
    if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    setEntries((prev) => prev.filter((e) => e.id !== id));

    // 只有 done 且有 uploadId 才需要後端清理；uploading/error 不需要
    if (entry.uploadStatus === 'done' && entry.uploadId && chatId) {
      apiService.removeUpload(chatId, entry.uploadId)
        .then(({ status, data }) => {
          if (status >= 200 && status < 300) return; // 成功，靜默
          // 409 session_asset_already_bound：靜默，代表訊息已送出，本地移除即可
          if (status === 409 && data?.detail?.code === 'session_asset_already_bound') return;
          // 503：後端清理服務未設定
          if (status === 503) {
            setRemoveNotice(`${entry.file.name} removed locally, but backend cleanup was not available.`);
            return;
          }
          // 其他非 2xx：顯示錯誤詳情
          const detail = typeof data?.detail === 'string'
            ? data.detail
            : (data?.detail?.code || JSON.stringify(data?.detail) || `HTTP ${status}`);
          setRemoveNotice(`${entry.file.name} removed locally, but backend cleanup failed: ${detail}`);
        })
        .catch(() => {
          // 網路錯誤等同後端清理不可用
          setRemoveNotice(`${entry.file.name} removed locally, but backend cleanup was not available.`);
        });
    }
  }, []);

  /**
   * 重試失敗的 entry。建立新的 AbortController 並重新上傳。
   */
  const retryEntry = useCallback(
    (id, chatId) => {
      const newController = new AbortController();
      // captured 讓我們在 state updater 外拿到更新後的 entry
      const captured = { entry: null };

      setEntries((prev) => {
        const existing = prev.find((e) => e.id === id && e.uploadStatus === 'error');
        if (!existing) return prev;
        const updated = {
          ...existing,
          uploadStatus: 'uploading',
          serverPath: null,
          errorMessage: null,
          abortController: newController,
        };
        captured.entry = updated;
        return prev.map((e) => (e.id === id ? updated : e));
      });

      // state updater 是同步呼叫，captured.entry 在此已設定
      if (captured.entry) {
        uploadEntry(captured.entry, chatId);
      }
    },
    [uploadEntry]
  );

  /**
   * 清空所有 entries：abort 進行中上傳，revoke 所有 blob URLs。
   */
  const clearAll = useCallback(() => {
    setEntries((prev) => {
      prev.forEach((e) => {
        if (e.uploadStatus === 'uploading') e.abortController.abort();
        if (e.previewUrl) URL.revokeObjectURL(e.previewUrl);
      });
      return [];
    });
  }, []);

  // unmount 時清理
  useEffect(() => () => clearAll(), [clearAll]);

  const isUploading = entries.some((e) => e.uploadStatus === 'uploading');
  const hasError = entries.some((e) => e.uploadStatus === 'error');
  const errorEntries = entries.filter((e) => e.uploadStatus === 'error');
  const allAttachmentUrls = entries
    .filter((e) => e.uploadStatus === 'done' && e.serverPath)
    .map((e) => e.serverPath);
  const allFiles = entries.map((e) => e.file);
  const totalCount = entries.length;

  return {
    entries,
    isUploading,
    hasError,
    errorEntries,
    allAttachmentUrls,
    allFiles,
    addFiles,
    removeEntry,
    retryEntry,
    clearAll,
    totalCount,
    /** remove API 失敗時的通知文字（null 表示無通知） */
    removeNotice,
    /** 清除 remove 通知 */
    clearRemoveNotice: () => setRemoveNotice(null),
    /** 重複檔案被阻擋時的通知文字（null 表示無通知） */
    duplicateNotice,
    /** 清除重複通知 */
    clearDuplicateNotice: () => setDuplicateNotice(null),
  };
}
